import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import { SHAPES, VIEWBOX, MAP_CREDIT } from './nigeriaMap';

/**
 * Businesses in the Data Hub: what members have listed in the business directory (voucha-community), as figures.
 * /data-hub/businesses has the whole picture; StatePage and the location guides show one place's, with
 * businessPanel(). Only what members list on Voucha: an industry with none is a potential gap, not a shortage.
 */
const api = (path, q) => app.forum.attribute('apiUrl') + '/voucha-community/businesses' + path + (q ? '?' + Object.entries(q).filter(([, v]) => v).map(([k, v]) => k + '=' + encodeURIComponent(v)).join('&') : '');
export const GAPS_NOTE = 'Counts are businesses listed on Voucha by its members. An industry with none listed is a potential gap, not proof that the place has none.';
const cache = {};
/** Coverage for a place ({ state } / { place: tag slug } / nothing for everywhere); cached for the visit */
export function coverage(q = {}) {
  const key = JSON.stringify(q);
  if (!(key in cache)) {
    cache[key] = undefined;
    app.request({
      method: 'GET',
      url: api('/coverage', q),
      errorHandler: () => {}
    }).then(r => cache[key] = r || null).catch(() => cache[key] = null).then(() => m.redraw());
  }
  return cache[key];
}
const go = url => e => {
  if (e && (e.ctrlKey || e.metaKey)) return;
  if (e) e.preventDefault();
  m.route.set(url);
};
const initials = n => String(n || '?').split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
const logo = b => b.logoUrl ? m("img", {
  className: "DHBiz-logo",
  src: b.logoUrl,
  alt: "",
  loading: "lazy"
}) : m("span", {
  className: "DHBiz-logo is-initials"
}, initials(b.name));

/** Industries as bars, most listed first; empty ones last and greyed */
export function industryBars(cov, dirQuery) {
  const cats = cov.categories.slice().sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...cats.map(c => c.count));
  return m("div", {
    className: "DHBiz-bars"
  }, cats.map(c => {
    const url = '/businesses?' + (dirQuery ? dirQuery + '&' : '') + 'category=' + c.key;
    return m("a", {
      className: 'DHBiz-bar' + (c.count ? '' : ' is-empty'),
      href: url,
      onclick: go(url)
    }, m("span", {
      className: "DHBiz-barLabel"
    }, m("i", {
      className: c.icon,
      "aria-hidden": "true"
    }), " ", c.name), m("span", {
      className: "DHBiz-barTrack"
    }, m("span", {
      style: {
        width: c.count / max * 100 + '%'
      }
    })), m("span", {
      className: "DHBiz-barN"
    }, c.count));
  }));
}
export function latestList(cov) {
  if (!cov.latest || !cov.latest.length) return null;
  return m("div", {
    className: "DHBiz-latest"
  }, cov.latest.map(b => m("a", {
    href: '/businesses/' + b.slug,
    onclick: go('/businesses/' + b.slug)
  }, logo(b), m("span", null, m("strong", null, b.name, b.verified ? m("i", {
    className: "fas fa-circle-check DHBiz-tick",
    "aria-label": "Verified"
  }) : null), m("small", null, [b.category, b.place].filter(Boolean).join(' · '))))));
}

/**
 * One place's businesses, for a state page or a location guide: totals, industries (and the potential gaps),
 * its cities, the latest listed, and links to the directory. q: { state: id } or { place: tag slug }.
 */
export function businessPanel(q, name, dirQuery) {
  const cov = coverage(q);
  if (cov === undefined) return m(LoadingIndicator, null);
  if (!cov) return m("p", {
    className: "DHMuted"
  }, "Businesses couldn\u2019t be loaded.");
  const dir = '/businesses?' + dirQuery;
  if (!cov.total) {
    return m("div", {
      className: "DHPanel DHBiz-empty"
    }, m("p", null, "No businesses are listed in ", name, " yet."), m("a", {
      className: "Button Button--primary",
      href: "/businesses/new",
      onclick: go('/businesses/new')
    }, "Add your business"));
  }
  return m("div", {
    className: "DHBiz-place"
  }, m("div", {
    className: "DHKpis"
  }, m("a", {
    className: "DHKpi",
    href: dir,
    onclick: go(dir)
  }, m("div", {
    className: "DHKpi-head"
  }, m("i", {
    className: "fas fa-store"
  }), " Listed"), m("div", {
    className: "DHKpi-value"
  }, cov.total)), m("div", {
    className: "DHKpi"
  }, m("div", {
    className: "DHKpi-head"
  }, m("i", {
    className: "fas fa-circle-check"
  }), " Verified"), m("div", {
    className: "DHKpi-value"
  }, cov.verified)), m("div", {
    className: "DHKpi"
  }, m("div", {
    className: "DHKpi-head"
  }, m("i", {
    className: "fas fa-layer-group"
  }), " Industries"), m("div", {
    className: "DHKpi-value"
  }, cov.categories.filter(c => c.count).length, m("small", null, " of ", cov.categories.length)))), m("div", {
    className: "DHPair"
  }, m("div", {
    className: "DHPanel"
  }, m("h3", null, "By industry"), industryBars(cov, dirQuery), cov.gaps && cov.gaps.length ? m("p", {
    className: "DHMuted DHBiz-gaps"
  }, m("strong", null, "Potential gaps:"), " ", cov.gaps.map(g => g.name).join(', '), ".") : null), m("div", {
    className: "DHPanel"
  }, m("h3", null, "Latest listed"), latestList(cov), cov.cities && cov.cities.length > 1 ? m("p", {
    className: "DHMuted DHBiz-cities"
  }, m("strong", null, "By city:"), " ", cov.cities.map(c => c.name + ' (' + c.count + ')').join(', ')) : null, m("a", {
    className: "DHLink",
    href: dir,
    onclick: go(dir)
  }, "All ", cov.total, " in the directory \u203A"))), m("p", {
    className: "DHMuted DHBiz-note"
  }, GAPS_NOTE));
}

/** /data-hub/businesses: listed businesses across Nigeria and abroad */
export default class BusinessesPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.bodyClass = 'DataHubFull';
    this.hover = null;
    app.setTitle('Businesses');
  }
  view() {
    const cov = coverage({});
    const states = cov ? cov.states.slice() : [];
    const bySlug = {};
    states.forEach(s => bySlug[s.slug] = s);
    const max = Math.max(1, ...states.map(s => s.count));
    const shade = n => !n ? 'DHMap-none' : 'DHMap-' + Math.min(5, Math.max(1, Math.ceil(n / max * 5)));
    const hovered = this.hover && bySlug[this.hover];
    const ng = states.reduce((t, s) => t + s.count, 0);
    const listed = states.filter(s => s.count).sort((a, b) => b.count - a.count);
    const none = states.filter(s => !s.count);
    const abroad = cov ? cov.countries.filter(c => c.code !== 'NG') : [];
    return m("div", {
      className: "DataHubFull-page"
    }, m("section", {
      className: "DHHero DHState-hero DHBiz-hero"
    }, m("div", {
      className: "container DHHero-inner"
    }, m("div", {
      className: "DHHero-text"
    }, m("a", {
      className: "DataHubPub-back",
      href: "/data-hub",
      onclick: go('/data-hub')
    }, m("i", {
      className: "fas fa-arrow-left"
    }), " Data Hub"), m("div", {
      className: "DHHero-eyebrow"
    }, "Businesses"), m("h1", null, "Where members\u2019 businesses are"), m("p", null, cov && !cov.total ? 'Businesses Voucha members list will show here by industry, state and country, once the first are listed.' : 'Businesses Voucha members have listed, by industry, state and country. Tap a state for its businesses.'), m("div", {
      className: "DHState-heroFacts"
    }, cov && !cov.total ? m("a", {
      className: "DHChip",
      href: "/businesses/new",
      onclick: go('/businesses/new')
    }, m("i", {
      className: "fas fa-plus"
    }), " Be the first: add your business") : null, cov && cov.total ? [m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-store"
    }), " ", m("strong", null, cov.total), " listed"), m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-circle-check"
    }), " ", m("strong", null, cov.verified), " verified"), m("span", {
      className: "DHChip"
    }, m("i", {
      className: "fas fa-map-marker-alt"
    }), " ", m("strong", null, listed.length), " of ", states.length, " states")] : null, m("a", {
      className: "DHChip",
      href: "/businesses",
      onclick: go('/businesses')
    }, m("i", {
      className: "fas fa-arrow-right"
    }), " Open the directory"))), m("div", {
      className: "DHState-locator DHBiz-map"
    }, m("svg", {
      viewBox: VIEWBOX,
      role: "img",
      "aria-label": "Businesses listed in each state"
    }, Object.entries(SHAPES).map(([slug, path]) => m("path", {
      d: path,
      className: shade(bySlug[slug] && bySlug[slug].count) + (this.hover === slug ? ' is-hover' : ''),
      onmouseenter: () => this.hover = slug,
      onmouseleave: () => this.hover = null,
      onclick: () => m.route.set('/data-hub/states/' + slug)
    }))), m("p", {
      className: "DHMuted DHState-hoverName"
    }, hovered ? hovered.name + ': ' + hovered.count + (hovered.count === 1 ? ' business' : ' businesses') : 'Darker states have more listed')))), cov && !cov.total ? null : m("div", {
      className: "container DHState"
    }, cov === undefined ? m(LoadingIndicator, null) : !cov ? m("p", {
      className: "DHMuted"
    }, "Businesses couldn\u2019t be loaded.") : [m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "By industry"), m("p", {
      className: "DHMuted"
    }, "Everywhere, Nigeria and abroad."))), m("div", {
      className: "DHPair"
    }, m("div", {
      className: "DHPanel"
    }, industryBars(cov, '')), m("div", {
      className: "DHPanel"
    }, m("h3", null, "Latest listed"), latestList(cov) || m("p", {
      className: "DHMuted"
    }, "None yet.")))), m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Nigeria\u2019s states"), m("p", {
      className: "DHMuted"
    }, ng, " listed in Nigeria. States with none yet are potential gaps."))), listed.length ? m("div", {
      className: "DHState-cards"
    }, listed.map(s => m("a", {
      className: "DHState-card",
      href: '/data-hub/states/' + s.slug,
      onclick: go('/data-hub/states/' + s.slug)
    }, m("span", {
      className: "DHState-cardIcon"
    }, m("i", {
      className: "fas fa-store"
    })), m("span", {
      className: "DHState-cardText"
    }, m("strong", null, s.slug === 'abuja' ? 'FCT Abuja' : s.name), m("span", {
      className: "DHMuted"
    }, s.count === 1 ? '1 business' : s.count + ' businesses'))))) : m("p", {
      className: "DHMuted"
    }, "No businesses listed in Nigeria yet."), none.length ? m("p", {
      className: "DHMuted DHBiz-gaps"
    }, m("strong", null, "None listed yet:"), " ", none.map(s => s.name).join(', '), ".") : null), abroad.length ? m("section", {
      className: "DHSection"
    }, m("div", {
      className: "DHSection-head"
    }, m("div", null, m("h2", null, "Abroad"), m("p", {
      className: "DHMuted"
    }, "Members\u2019 businesses outside Nigeria."))), m("div", {
      className: "DHKpis"
    }, abroad.map(c => m("a", {
      className: "DHKpi",
      href: '/businesses?country=' + c.code,
      onclick: go('/businesses?country=' + c.code)
    }, m("div", {
      className: "DHKpi-head"
    }, c.name), m("div", {
      className: "DHKpi-value"
    }, c.count))))) : null, m("p", {
      className: "DataHubPub-source"
    }, GAPS_NOTE, " Businesses are checked by Voucha\u2019s moderators before they\u2019re listed; \u201Cverified\u201D means a moderator checked the registration (CAC in Nigeria). ", MAP_CREDIT, ".")]));
  }
}

/**
 * A location guide's businesses section, written in the guide's own format (its classes: sec-head, stats, bars,
 * chips, cta), so it reads as one more numbered section of the guide. Null until loaded.
 */
export function guideBusinesses(tag, name, number) {
  const cov = coverage({
    place: tag
  });
  if (!cov) return null;
  const dir = '/businesses?place=' + encodeURIComponent(tag);
  const cats = cov.categories.filter(c => c.count).sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...cats.map(c => c.count));
  const eyebrow = (number ? number + ' · ' : '') + 'Members’ businesses here';
  return m("section", {
    className: "DHGuideBiz"
  }, m("div", {
    className: "sec-head"
  }, m("div", {
    className: "eyebrow"
  }, eyebrow), m("h2", null, cov.total ? (cov.total === 1 ? 'One business' : cov.total + ' businesses') + ' run by Voucha members in ' + name : 'Run a business in ' + name + '?'), m("p", null, cov.total ? 'Listed in the Voucha business directory and checked by our moderators, by industry and city.' : 'No member has listed one here yet. List yours and Nigerians in ' + name + ' looking for it will find you.')), cov.total ? m("div", {
    className: "stats"
  }, m("div", {
    className: "stat"
  }, m("b", null, cov.total), m("span", null, "listed by members")), m("div", {
    className: "stat"
  }, m("b", null, cov.verified), m("span", null, "verified registration")), m("div", {
    className: "stat"
  }, m("b", null, cats.length), m("span", null, "of ", cov.categories.length, " industries"))) : null, cats.length ? m("div", {
    className: "bars"
  }, cats.map(c => m("a", {
    className: "bar DHGuideBiz-bar",
    href: dir + '&category=' + c.key,
    onclick: go(dir + '&category=' + c.key)
  }, m("div", {
    className: "lab"
  }, c.name), m("div", {
    className: "track"
  }, m("div", {
    className: "fill",
    style: {
      width: c.count / max * 100 + '%'
    }
  })), m("div", {
    className: "val"
  }, c.count)))) : null, cov.cities && cov.cities.length ? m("div", {
    className: "chips"
  }, cov.cities.map(c => m("span", {
    className: "chip"
  }, c.name, " \xB7 ", c.count))) : null, cov.latest && cov.latest.length ? m("div", {
    className: "DHGuideBiz-latest"
  }, cov.latest.map(b => m("a", {
    href: '/businesses/' + b.slug,
    onclick: go('/businesses/' + b.slug)
  }, logo(b), m("span", null, m("strong", null, b.name, b.verified ? m("i", {
    className: "fas fa-circle-check DHBiz-tick",
    "aria-label": "Verified"
  }) : null), m("small", null, [b.category, b.place].filter(Boolean).join(' · ')))))) : null, cov.total && cov.gaps && cov.gaps.length ? m("p", {
    className: "note"
  }, "Potential gaps (no member business listed here yet): ", cov.gaps.map(g => g.name).join(', '), ".") : null, m("div", {
    className: "cta"
  }, m("a", {
    href: cov.total ? dir : '/businesses/new',
    onclick: go(cov.total ? dir : '/businesses/new')
  }, cov.total ? 'See them in the directory' : 'Add your business')));
}